import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Visualizador de imagem (2026-09-02): clicar numa imagem de conteudo rico
 * FORA de edicao abre a imagem num modal escuro onde da para dar zoom com o
 * scroll do mouse (centrado no cursor) e arrastar para mover. Duplo clique
 * volta ao enquadramento; Esc, X ou clicar no fundo fecham.
 *
 * Monte UMA VEZ na raiz da aplicacao (`<ImageViewerHost />`): ele observa os
 * cliques do documento em `.ob-rte-body img` — descricao, resultado esperado,
 * instrucoes, comentarios etc. Em edicao (contentEditable) o clique continua
 * selecionando a imagem para redimensionar, entao o visualizador nao abre.
 */
/**
 * Abre o visualizador sem clique numa imagem (ex.: item "Ver foto" de um
 * menu). Vai por evento porque o host vive na raiz da aplicacao, longe de quem
 * chama.
 */
const EVENTO_AMPLIAR = 'ob:ampliar-imagem'

export function ampliarImagem(src: string): void {
  window.dispatchEvent(new CustomEvent<string>(EVENTO_AMPLIAR, { detail: src }))
}

export function ImageViewerHost() {
  const [src, setSrc] = useState<string | null>(null)
  const [scale, setScale] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const quadroRef = useRef<HTMLDivElement>(null)
  const arrasto = useRef<{ x: number; y: number; px: number; py: number } | null>(null)
  const arrastou = useRef(false)

  // clique em qualquer imagem de conteudo (fora de edicao) abre o visualizador
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const alvo = e.target
      if (!(alvo instanceof HTMLImageElement)) return
      // Conteudo rico OU qualquer imagem marcada com `data-ob-ampliar` (foto de
      // perfil, banner): clicar mostra grande no meio da tela, clicar fora
      // fecha. Pedido do usuario, 2026-09-16.
      const ampliavel = alvo.closest('.ob-rte-body') !== null || alvo.closest('[data-ob-ampliar]') !== null
      if (!ampliavel) return
      if (alvo.closest('[contenteditable]')) return
      e.preventDefault()
      setScale(1)
      setPos({ x: 0, y: 0 })
      setSrc(alvo.currentSrc || alvo.src)
    }
    const onAmpliar = (e: Event) => {
      const src = (e as CustomEvent<string>).detail
      if (typeof src !== 'string' || src === '') return
      setScale(1)
      setPos({ x: 0, y: 0 })
      setSrc(src)
    }
    document.addEventListener('click', onClick)
    window.addEventListener(EVENTO_AMPLIAR, onAmpliar)
    return () => {
      document.removeEventListener('click', onClick)
      window.removeEventListener(EVENTO_AMPLIAR, onAmpliar)
    }
  }, [])

  // Esc fecha; trava o scroll da pagina enquanto aberto
  useEffect(() => {
    if (!src) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setSrc(null)
      }
    }
    document.addEventListener('keydown', onKey, true)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = overflow
    }
  }, [src])

  // zoom com o scroll, centrado no cursor — listener nativo porque o wheel
  // delegado do React e passivo e nao deixa dar preventDefault
  useEffect(() => {
    const quadro = quadroRef.current
    if (!src || !quadro) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setScale((s) => {
        const novo = Math.min(10, Math.max(0.2, s * (e.deltaY < 0 ? 1.15 : 1 / 1.15)))
        const f = novo / s
        const r = quadro.getBoundingClientRect()
        const cx = r.left + r.width / 2
        const cy = r.top + r.height / 2
        setPos((p) => ({
          x: e.clientX - cx - f * (e.clientX - cx - p.x),
          y: e.clientY - cy - f * (e.clientY - cy - p.y),
        }))
        return novo
      })
    }
    quadro.addEventListener('wheel', onWheel, { passive: false })
    return () => quadro.removeEventListener('wheel', onWheel)
  }, [src])

  // arrastar para mover
  useEffect(() => {
    if (!src) return
    const onMove = (e: MouseEvent) => {
      if (!arrasto.current) return
      e.preventDefault()
      const { x, y, px, py } = arrasto.current
      if (Math.abs(e.clientX - x) + Math.abs(e.clientY - y) > 3) arrastou.current = true
      setPos({ x: px + (e.clientX - x), y: py + (e.clientY - y) })
    }
    const onUp = () => {
      arrasto.current = null
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [src])

  if (!src) return null

  const aplicarZoom = (f: number) => setScale((s) => Math.min(10, Math.max(0.2, s * f)))
  const resetar = () => {
    setScale(1)
    setPos({ x: 0, y: 0 })
  }

  return createPortal(
    <div
      ref={quadroRef}
      className="ob-imgviewer"
      role="dialog"
      aria-modal="true"
      aria-label="Visualizador de imagem"
      onClick={() => {
        // clique seco no fundo fecha; fim de arrasto nao
        if (arrastou.current) {
          arrastou.current = false
          return
        }
        setSrc(null)
      }}
    >
      <div className="ob-imgviewer__topo" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="ob-imgviewer__btn" title="Reduzir" aria-label="Reduzir" onClick={() => aplicarZoom(1 / 1.25)}>
          <i className="fas fa-search-minus" aria-hidden />
        </button>
        <span className="ob-imgviewer__zoom" title="Voltar a 100%" onDoubleClick={resetar}>
          {Math.round(scale * 100)}%
        </span>
        <button type="button" className="ob-imgviewer__btn" title="Ampliar" aria-label="Ampliar" onClick={() => aplicarZoom(1.25)}>
          <i className="fas fa-search-plus" aria-hidden />
        </button>
        <button type="button" className="ob-imgviewer__btn" title="Enquadrar (100%)" aria-label="Enquadrar" onClick={resetar}>
          <i className="fas fa-compress-arrows-alt" aria-hidden />
        </button>
        <span className="ob-imgviewer__sep" />
        <button type="button" className="ob-imgviewer__btn" title="Fechar (Esc)" aria-label="Fechar" onClick={() => setSrc(null)}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>
      <img
        src={src}
        alt=""
        draggable={false}
        className="ob-imgviewer__img"
        style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, cursor: arrasto.current ? 'grabbing' : 'grab' }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={resetar}
        onMouseDown={(e) => {
          e.preventDefault()
          arrastou.current = false
          arrasto.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y }
          document.body.style.userSelect = 'none'
        }}
      />
    </div>,
    document.body,
  )
}
